import { PlusIcon, Trash2Icon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type SuggestedNext = (values: string[]) => string | null | undefined

// IDs of the same corridor share prefix and zero-padded length, so length + lexicographic order is numeric order.
function compareIds(a: string, b: string) {
  return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0)
}

export interface InternationalShipmentIdsEditorProps {
  values: string[]
  corridor: string | null
  description: string
  suggestedNext?: SuggestedNext
  nextValue?: string | null
  /** Shown when the list is empty to explain that the server will reserve this ID on save. */
  autoAssignValue?: string | null
  disabled?: boolean
  onChange: (values: string[]) => void
  onRemove?: () => void
  showWarning?: boolean
  className?: string
}

export function InternationalShipmentIdsEditor({
  values,
  corridor,
  description,
  suggestedNext,
  nextValue,
  autoAssignValue,
  disabled = false,
  onChange,
  onRemove,
  showWarning = false,
  className,
}: InternationalShipmentIdsEditorProps) {
  // The local suggestion only sees this shipment's values; the server value accounts for
  // every assigned ID and the corridor sequence. Use whichever is further ahead.
  const getNextValue = () => [suggestedNext?.(values), nextValue]
    .filter((value): value is string => Boolean(value) && !values.includes(value as string))
    .reduce((maximum, value) => compareIds(value, maximum) > 0 ? value : maximum, "")
  const canAddNext = Boolean(getNextValue())

  return (
    <div className={cn("grid gap-1.5 rounded-md border border-purple-200 bg-purple-50/50 p-3", className)}>
      <Label>IDs internacionales</Label>
      {corridor && <p className="text-xs font-medium text-purple-700">Corredor: {corridor}</p>}
      <p className="text-[11px] text-muted-foreground">{description}</p>
      {showWarning && !canAddNext && (
        <p className="rounded bg-amber-100 px-2 py-1.5 text-xs text-amber-800">
          No hay un corredor internacional configurado para este origen y destino. No se puede generar un ID automáticamente.
        </p>
      )}
      {values.length === 0 && autoAssignValue && (
        <p className="rounded bg-purple-100 px-2 py-1.5 text-xs text-purple-800">
          Se asignará automáticamente al guardar: <span className="font-mono font-medium">{autoAssignValue}</span>
        </p>
      )}
      <div className="space-y-1.5">
        {values.map((value, index) => (
          <div key={`international-id-${index}`} className="flex items-center gap-1.5">
            <Input
              value={value}
              disabled={disabled}
              onChange={(event) => {
                const nextValues = [...values]
                nextValues[index] = event.target.value.toUpperCase()
                onChange(nextValues)
              }}
              className="h-8 font-mono text-xs"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive"
              disabled={disabled}
              onClick={() => { onRemove?.(); onChange(values.filter((_, valueIndex) => valueIndex !== index)) }}
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        disabled={disabled || !canAddNext}
        onClick={() => {
          const value = getNextValue()
          if (value) onChange([...values, value])
        }}
      >
        <PlusIcon className="size-3.5 mr-1" /> Agregar siguiente
      </Button>
    </div>
  )
}
